// src/pages/CarrierProfile.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDoc, doc, collection, query, where, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import VerifiedBadge from '../components/VerifiedBadge';
import RegisterCheck from '../components/RegisterCheck';
import LevelBadge from '../components/LevelBadge';
import BadgeShelf from '../components/BadgeShelf';
import './CarrierProfile.css';

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const CarrierProfile = () => {
  const { carrierId } = useParams();
  const navigate = useNavigate();

  const [carrier, setCarrier] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [rank, setRank] = useState(null);
  // True only when this carrier is the only publicCarriers doc at their credit total.
  const [soleAtCredits, setSoleAtCredits] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (carrier?.name) document.title = `${carrier.name} — The WasteGame`;
  }, [carrier?.name]);

  useEffect(() => {
    if (!carrierId) return;
    let cancelled = false;

    const fetchAll = async () => {
      try {
        // Public carrier fields only (publicCarriers is kept in sync by a Cloud
        // Function). The carrier's users doc is private to them and admins.
        const userSnap = await getDoc(doc(db, 'publicCarriers', carrierId));
        if (cancelled) return;
        if (!userSnap.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const carrierData = { id: userSnap.id, ...userSnap.data() };
        setCarrier(carrierData);

        // Fetch reviews
        const reviewsSnap = await getDocs(
          query(collection(db, 'reviews'), where('providerId', '==', carrierId))
        );
        if (cancelled) return;
        const reviewList = reviewsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        reviewList.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() || 0;
          const tb = b.createdAt?.toMillis?.() || 0;
          return tb - ta;
        });
        setReviews(reviewList);

        // Rank = 1 + number of carriers with more Eco Credits.
        // A count query, so other carriers' docs are not downloaded.
        const myCredits = carrierData.credits || 0;
        const hasCredits = Number(myCredits) > 0;
        const aheadSnap = await getCountFromServer(
          query(collection(db, 'publicCarriers'), where('credits', '>', myCredits))
        );
        if (cancelled) return;
        const nextRank = aheadSnap.data().count + 1;
        setRank(nextRank);

        // The Top N banner needs a sole leader. Carriers on 0 credits, and
        // anyone sharing a credit total, are not shown as Top 1/2/3.
        // The extra count is only needed when the greater-than count is
        // already a top-3 place; a lower place cannot show the banner.
        setSoleAtCredits(false);
        if (hasCredits && nextRank <= 3) {
          const sameSnap = await getCountFromServer(
            query(collection(db, 'publicCarriers'), where('credits', '==', myCredits))
          );
          if (cancelled) return;
          setSoleAtCredits(sameSnap.data().count === 1);
        }
      } catch (err) {
        console.error('Error loading carrier profile:', err);
      }
      if (!cancelled) setLoading(false);
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [carrierId]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <p className="text-muted" style={{ marginTop: '60px', textAlign: 'center' }}>Loading carrier profile...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="page-wrapper">
        <div className="cp-not-found">
          <p style={{ fontSize: '3rem' }}>🚫</p>
          <h2>Carrier Not Found</h2>
          <p className="text-muted">This carrier profile doesn't exist or has been removed.</p>
          <button className="btn btn-secondary" style={{ marginTop: '16px' }} onClick={() => navigate(-1)}>← Go Back</button>
        </div>
      </div>
    );
  }

  const avgRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  const myCredits = carrier.credits || 0;
  const showTopBanner = Number(myCredits) > 0 && soleAtCredits && rank >= 1 && rank <= 3;
  const top3Medal = showTopBanner ? (rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉') : null;

  return (
    <div className="page-wrapper">
      <button className="cp-back-btn" onClick={() => navigate(-1)}>← Back</button>

      {top3Medal && (
        <div className="cp-top3-banner">
          <span className="cp-top3-medal">{top3Medal}</span>
          <div>
            <div className="cp-top3-title">Top {rank} Carrier on WasteGame</div>
            <div className="cp-top3-sub">Ranked #{rank} by lifetime Eco Credits — one of the most trusted carriers on the platform</div>
          </div>
        </div>
      )}

      <div className="cp-grid">
        {/* LEFT / MAIN */}
        <div>
          {/* HERO CARD */}
          <div className="card cp-hero">
            <div className="cp-avatar">{carrier.name?.charAt(0) || '?'}</div>
            <div className="cp-hero-info">
              <h1 className="cp-name">{carrier.name}</h1>
              {carrier.verified
                ? <VerifiedBadge size="md" />
                : <span className="badge badge-muted">Approval pending</span>
              }
              <div style={{ marginTop: '6px' }}>
                <LevelBadge jobs={carrier.jobs || 0} size="md" showProgress />
              </div>
              {/* Only public fields are shown here: short name, verified tick, level,
                  rating, jobs, credits. Contact details, licence number, location,
                  vehicle, bio and photo stay private. */}
            </div>
          </div>

          {/* STATS */}
          <div className="card cp-stats-card">
            <div className="cp-stats">
              <div className="cp-stat">
                <div className="cp-stat-val">{avgRating || '—'}</div>
                <div className="cp-stat-lbl">Rating</div>
              </div>
              <div className="cp-stat">
                <div className="cp-stat-val">{reviews.length}</div>
                <div className="cp-stat-lbl">Reviews</div>
              </div>
              <div className="cp-stat">
                <div className="cp-stat-val">{carrier.jobs || 0}</div>
                <div className="cp-stat-lbl">Jobs Done</div>
              </div>
              <div className="cp-stat cp-stat-teal">
                <div className="cp-stat-val">{carrier.credits || 0}</div>
                <div className="cp-stat-lbl">Eco Credits</div>
              </div>
              {rank != null && (
                <div className="cp-stat cp-stat-amber">
                  <div className="cp-stat-val">{Number(myCredits) > 0 ? `#${rank}` : '—'}</div>
                  <div className="cp-stat-lbl">Eco Rank</div>
                </div>
              )}
            </div>

          </div>

          {/* BADGES */}
          <div className="card" style={{ marginBottom: '14px' }}>
            <BadgeShelf provider={carrier} />
          </div>

          {/* REVIEWS */}
          <div className="card">
            <h2 className="card-title">CUSTOMER <span>REVIEWS</span></h2>
            {reviews.length === 0 ? (
              <div className="cp-no-reviews">
                <p style={{ fontSize: '2rem', marginBottom: '8px' }}>⭐</p>
                <p className="text-muted">No reviews yet — this carrier hasn't completed a job through WasteGame yet.</p>
              </div>
            ) : (
              reviews.map(r => (
                <div key={r.id} className="review-item">
                  <div className="review-header">
                    <div className="review-stars">
                      {[1, 2, 3, 4, 5].map(n => (
                        <span key={n} style={{ color: n <= r.rating ? 'var(--amber)' : 'rgba(244,196,48,0.2)' }}>★</span>
                      ))}
                      <span className="review-score">{r.rating}/5</span>
                    </div>
                    <span className="review-date">{formatDate(r.createdAt)}</span>
                  </div>
                  {r.review && <p className="review-text">"{r.review}"</p>}
                </div>
              ))
            )}
          </div>
        </div>

        {/* SIDEBAR */}
        <div>
          <div className="card cp-trust-card">
            <h3 className="card-title-sm">TRUST & <span>SAFETY</span></h3>
            <div className="protection-list">
              {carrier.verified
                ? <div className="prot-item">✅ Registration checked by WasteGame. <RegisterCheck /></div>
                : <div className="prot-item" style={{ color: 'var(--amber)' }}>⏳ Approval pending</div>
              }
              <div className="prot-item">🛡️ Duty of care stays with the customer</div>
              <div className="prot-item">🚫 Fly-tipping can get a carrier removed</div>
              <div className="prot-item">📋 Waste transfer notes are a record, not a guarantee</div>
              <div className="prot-item">⭐ Reviews from completed jobs only</div>
            </div>
          </div>

          {avgRating && (
            <div className="card cp-rating-card" style={{ marginTop: '14px' }}>
              <div className="cp-big-rating">{avgRating}</div>
              <div className="cp-big-stars">
                {[1, 2, 3, 4, 5].map(n => (
                  <span key={n} style={{ fontSize: '1.4rem', color: n <= Math.round(avgRating) ? 'var(--amber)' : 'rgba(244,196,48,0.2)' }}>★</span>
                ))}
              </div>
              <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: '6px' }}>
                Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarrierProfile;
